/**
 * Persistencia del viewport del mapa (cantón actual, centro y zoom) por **sesión**.
 *
 * Se usa `sessionStorage` a propósito: el viewport se recuerda mientras la
 * pestaña/navegador siga abierto (sobrevive a recargas F5) y se limpia solo
 * al cerrar el navegador. Así el mapa vuela una sola vez en el primer acceso
 * y luego respeta el zoom con el que el usuario dejó el mapa.
 *
 * Mantiene los nombres de clave existentes para no romper datos en vuelo.
 * Nota: `lng/lat/zoom` usan el prefijo legado `ArboEc_` (sin "l").
 */
const VIEWPORT_KEYS = {
  city: 'ArbolEc_city',
  lng: 'ArboEc_map_lng',
  lat: 'ArboEc_map_lat',
  zoom: 'ArboEc_map_zoom',
}

const getSessionStorage = () =>
  typeof window !== 'undefined' ? window.sessionStorage : null

/**
 * Lee el viewport guardado para la sesión.
 * @returns {{ city: string, center: [number, number], zoom: number } | null}
 *   null si falta algún dato o es inválido.
 */
export const readSavedViewport = () => {
  const storage = getSessionStorage()
  if (!storage) return null

  const city = storage.getItem(VIEWPORT_KEYS.city)
  const lng = parseFloat(storage.getItem(VIEWPORT_KEYS.lng))
  const lat = parseFloat(storage.getItem(VIEWPORT_KEYS.lat))
  const zoom = parseFloat(storage.getItem(VIEWPORT_KEYS.zoom))

  if (!city || !Number.isFinite(lng) || !Number.isFinite(lat) || !Number.isFinite(zoom)) {
    return null
  }

  return { city, center: [lng, lat], zoom }
}

/**
 * Guarda el viewport actual. Ignora valores no finitos o `city` vacío para no
 * persistir basura que invalide la restauración posterior.
 * @param {{ city: string, center: [number, number], zoom: number }} viewport
 */
export const saveViewport = ({ city, center, zoom } = {}) => {
  const storage = getSessionStorage()
  if (!storage || !city) return

  const lng = center?.[0]
  const lat = center?.[1]
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || !Number.isFinite(zoom)) return

  storage.setItem(VIEWPORT_KEYS.lng, lng.toFixed(4))
  storage.setItem(VIEWPORT_KEYS.lat, lat.toFixed(4))
  storage.setItem(VIEWPORT_KEYS.zoom, zoom.toFixed(2))
  storage.setItem(VIEWPORT_KEYS.city, city)
}

/**
 * Indica si hay un viewport guardado en la sesión para el cantón dado.
 * Se usa para no re-volar (respetar el zoom del usuario) en recargas.
 * @param {string} city slug del cantón
 * @returns {boolean}
 */
export const hasSavedViewportForCity = city => {
  if (!city) return false
  const saved = readSavedViewport()
  return Boolean(saved && saved.city === city)
}

/**
 * Limpia el viewport guardado. Borra la sesión y, como limpieza de migración,
 * las claves legadas que antes vivían en `localStorage`.
 */
export const clearSavedViewport = () => {
  const keys = Object.values(VIEWPORT_KEYS)
  const session = getSessionStorage()
  if (session) keys.forEach(key => session.removeItem(key))
  if (typeof window !== 'undefined' && window.localStorage) {
    keys.forEach(key => window.localStorage.removeItem(key))
  }
}
